import { Box, Group, Paper, Text, Textarea, TextInput, Tooltip, UnstyledButton, type TextInputProps } from '@mantine/core';
import { IconLanguage } from '@tabler/icons-react';
import { useState, type ReactNode } from 'react';
import { BilingualScope, useBilingualScope, type Bilingual, type Lang } from '../components/bilingual';
import { tr } from '../lib/i18n';

export { BilingualScope, useBilingualScope };
export type { Bilingual, Lang };
export { bi, emptyBilingual, fromLocalized, toLocalized, isComplete, isHalfFilled, missingLang } from '../components/bilingual';

/** The shared editing language of the scope, or a local one outside a scope. */
function useLang(): [Lang, (l: Lang) => void] {
  const ctx = useBilingualScope();
  const [local, setLocal] = useState<Lang>('ar');
  return ctx ? [ctx.lang, ctx.setLang] : [local, setLocal];
}

/** «ع | EN» switch inside a field; a red dot marks the language that is still empty. */
export function LangSwitch({ value, lang, onLang, required, disabled }: { value: Bilingual; lang: Lang; onLang: (l: Lang) => void; required: boolean; disabled?: boolean }) {
  const item = (l: Lang, text: string) => {
    const empty = !value[l].trim();
    const otherFilled = !!value[l === 'ar' ? 'en' : 'ar'].trim();
    const warn = empty && (required || otherFilled);
    const active = lang === l;
    return (
      <Tooltip key={l} label={warn ? (l === 'ar' ? tr('النص العربي ناقص', 'Arabic text is missing') : tr('النص الإنجليزي ناقص', 'English text is missing')) : l === 'ar' ? 'العربية' : 'English'}>
        <UnstyledButton
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onLang(l)}
          aria-pressed={active}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, height: 22, paddingInline: 8, borderRadius: 6,
            fontSize: 11.5, fontWeight: 700, lineHeight: 1,
            color: active ? '#fff' : 'var(--pf-muted)', background: active ? 'var(--mantine-color-ink-9)' : 'transparent',
          }}
        >
          {text}
          {warn && <Box component="span" w={5} h={5} bg="red.6" style={{ borderRadius: 9 }} />}
        </UnstyledButton>
      </Tooltip>
    );
  };
  return (
    <Group gap={2} p={2} dir="ltr" wrap="nowrap" style={{ border: '1px solid var(--pf-line)', borderRadius: 8, background: '#fff' }}>
      {item('ar', 'ع')}
      {item('en', 'EN')}
    </Group>
  );
}

export type BilingualFieldProps = Omit<TextInputProps, 'value' | 'onChange' | 'defaultValue' | 'label' | 'error'> & {
  label: ReactNode;
  value: Bilingual;
  onChange: (v: Bilingual) => void;
  /** both languages required (default). false = optional, but never half-filled */
  required?: boolean;
  errorAr?: string;
  errorEn?: string;
  multiline?: boolean;
  minRows?: number;
};

/** One input that holds Arabic + English; every field in a scope flips language together. */
export function BilingualField({ value, onChange, required = true, errorAr, errorEn, label, description, disabled, multiline, minRows = 2, placeholder, ...props }: BilingualFieldProps) {
  const [lang, setLang] = useLang();
  const other: Lang = lang === 'ar' ? 'en' : 'ar';
  const otherMissing = !value[other].trim() && (required || !!value[lang].trim());
  const err = lang === 'ar' ? errorAr : errorEn;
  const otherErr = lang === 'ar' ? errorEn : errorAr;
  const hint = otherErr
    ? `${other === 'en' ? tr('الإنجليزي', 'English') : tr('العربي', 'Arabic')}: ${otherErr}`
    : otherMissing && value[lang].trim()
      ? other === 'en' ? tr('ينقصه النص الإنجليزي — اضغط EN للترجمة', 'English text missing — click EN to translate') : tr('ينقصه النص العربي — اضغط ع', 'Arabic text missing — click ع')
      : undefined;
  const common = {
    label: (
      <span>
        {label} <Text span size="xs" c="dimmed" fw={400}>{lang === 'ar' ? tr('(عربي)', '(Arabic)') : '(English)'}</Text>
      </span>
    ),
    withAsterisk: required,
    value: value[lang],
    disabled,
    error: err ?? (otherErr ? hint : undefined),
    description: !err && !otherErr && hint ? <Text span size="xs" c="orange.7">{hint}</Text> : description,
    placeholder: lang === 'en' && value.ar ? value.ar : placeholder,
    dir: lang === 'ar' ? 'rtl' : 'ltr',
    lang,
    rightSectionWidth: 74,
    rightSection: <LangSwitch value={value} lang={lang} onLang={setLang} required={required} disabled={disabled} />,
    rightSectionPointerEvents: 'all' as const,
    styles: { section: { insetInlineEnd: 4, insetInlineStart: 'auto' } },
  };
  return multiline ? (
    <Textarea {...(props as object)} {...common} minRows={minRows} onChange={(e) => onChange({ ...value, [lang]: e.currentTarget.value })} />
  ) : (
    <TextInput {...props} {...common} onChange={(e) => onChange({ ...value, [lang]: e.currentTarget.value })} />
  );
}

/** Scope header: flip every field's language + how many translations are missing. */
export function BilingualToolbar({ values = [], counts }: { values?: { value: Bilingual; required?: boolean }[]; counts?: { ar: number; en: number } }) {
  const ctx = useBilingualScope();
  const missingEn = counts?.en ?? values.filter((v) => !v.value.en.trim() && (v.required !== false || !!v.value.ar.trim())).length;
  const missingAr = counts?.ar ?? values.filter((v) => !v.value.ar.trim() && (v.required !== false || !!v.value.en.trim())).length;
  if (!ctx) return null;
  return (
    <Paper withBorder radius="md" px="sm" py={8} bg="var(--pf-hover)">
      <Group gap="sm" wrap="nowrap">
        <IconLanguage size={17} stroke={1.6} color="var(--pf-muted)" />
        <Text size="sm" style={{ flex: 1 }}>
          {missingAr + missingEn === 0
            ? tr('النصوص جاهزة باللغتين', 'All texts are complete in both languages')
            : [missingAr && tr(`${missingAr} بدون عربي`, `${missingAr} missing Arabic`), missingEn && tr(`${missingEn} بدون إنجليزي`, `${missingEn} missing English`)].filter(Boolean).join(' — ')}
        </Text>
        <LangSwitch value={{ ar: missingAr ? '' : 'x', en: missingEn ? '' : 'x' }} lang={ctx.lang} onLang={ctx.setLang} required />
      </Group>
    </Paper>
  );
}
